// The Review keyboard, as a pure function of the event.
//
// This is the screen the Author spends the most time in, so the whole pass —
// move, edit, approve, re-run, listen — is reachable without the mouse. The
// mapping is separated from the screen so every key can be tested without a
// DOM, and so the shortcuts overlay is generated from the same list the
// handler dispatches on rather than a second copy of it that drifts.

import type { ViewMode } from "./viewMode";
import { VIEW_MODE_DIGITS } from "./viewMode";

export type ShortcutAction =
  | { readonly kind: "focusNext" }
  | { readonly kind: "focusPrevious" }
  | { readonly kind: "edit" }
  | { readonly kind: "approve" }
  | { readonly kind: "instruction" }
  | { readonly kind: "playSample" }
  | { readonly kind: "help" }
  | { readonly kind: "viewMode"; readonly mode: ViewMode };

/** The slice of a keyboard event the mapping reads. */
export interface KeyEventLike {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
}

/**
 * The single-key shortcuts, live only when no modifier is down: a Control or
 * Command chord belongs to the browser (Command-R reloads), and stealing one
 * to focus the Instruction field would be a worse surprise than any keystroke
 * saved. Shift is allowed through only for `?`, which needs it on most
 * layouts.
 */
export function shortcutFor(event: KeyEventLike): ShortcutAction | null {
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (event.key === "?") return { kind: "help" };
  if (event.shiftKey) return null;
  const mode = VIEW_MODE_DIGITS[event.key];
  if (mode) return { kind: "viewMode", mode };
  switch (event.key) {
    case "j":
      return { kind: "focusNext" };
    case "k":
      return { kind: "focusPrevious" };
    case "e":
      return { kind: "edit" };
    case "a":
      return { kind: "approve" };
    case "r":
      return { kind: "instruction" };
    case "p":
      return { kind: "playSample" };
    default:
      return null;
  }
}

/** Command or Control with Enter: the commit of an open editor, on either platform. */
export const isCommitChord = (event: KeyEventLike): boolean =>
  event.key === "Enter" && (event.metaKey || event.ctrlKey);

/**
 * Whether the keystroke belongs to something the Author is typing in. A
 * shortcut that fired while the Instruction field had focus would turn "reach
 * for a quieter stroke" into a stream of approvals, so the screen ignores its
 * own keys whenever the event came from a field or anything editable.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  // `typeof` first: this module is imported by tests and by the server-side
  // bundle check, where HTMLElement is not a global and a bare `instanceof`
  // would throw a ReferenceError rather than answer "no".
  if (typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable ||
    target.closest("[data-typing]") !== null
  );
}
